import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/ui/Table";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import {
  createTeam,
  deleteTeam,
  getSentTeamJoinRequests,
  getTeams,
  joinTeam,
} from "./teamService";
import TeamEditPage from "./TeamEditPage";

const TeamListPage = () => {
  const api = useApi();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createAdminType, setCreateAdminType] = useState("SPORTSMAN");
  const [createFile, setCreateFile] = useState(null);
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinNotice, setJoinNotice] = useState("");
  const [sentJoinRequests, setSentJoinRequests] = useState([]);
  const [teamFilter, setTeamFilter] = useState("all");
  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const data = await getTeams(api);
        if (!mounted) return;
        setRows(data || []);
      } catch (err) {
        if (!mounted) return;
        setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token]);

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleMenuToggle = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((current) => (current === id ? null : id));
  };

  const handleEditTeam = (e, row) => {
    e.stopPropagation();
    setEditingTeam(row);
  };

  const handleTeamSaved = (updatedTeam) => {
    if (!updatedTeam?.publicId) return;
    setRows((current) =>
      (current || []).map((row) =>
        row.publicId === updatedTeam.publicId ? { ...row, ...updatedTeam } : row,
      ),
    );
    setEditingTeam((current) =>
      current?.publicId === updatedTeam.publicId
        ? { ...current, ...updatedTeam }
        : current,
    );
  };

  const handleCopyTeamId = async (e, row) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(row.inviteCode || "");
    } catch {
      // ignore copy errors
    }
  };

  const handleRemoveTeam = (e, row) => {
    e.stopPropagation();
    setDeleteError("");
    setConfirmDeleteTeam(row);
    setOpenMenuId(null);
  };

  const handleOpenCreate = () => {
    setCreateName("");
    setCreateAdminType("SPORTSMAN");
    setCreateFile(null);
    setCreateError("");
    setCreateOpen(true);
  };

  const handleOpenJoin = () => {
    setJoinCode("");
    setJoinError("");
    setJoinNotice("");
    setJoinOpen(true);
  };

  const handleJoinTeam = async () => {
    const code = joinCode.trim();
    if (!code) {
      setJoinError("Invite code is required.");
      return;
    }
    setJoining(true);
    setJoinError("");
    try {
      await joinTeam(api, { code, token });
      const [teams, requests] = await Promise.all([
        getTeams(api),
        getSentTeamJoinRequests(api),
      ]);
      setRows(teams || []);
      setSentJoinRequests(requests || []);
      setJoinNotice("Join request sent.");
      setJoinOpen(false);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not join team. Please try again.";
      setJoinError(message);
    } finally {
      setJoining(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!createName.trim()) {
      setCreateError("Team name is required.");
      return;
    }
    if (!createAdminType) {
      setCreateError("Admin member type is required.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const created = await createTeam(api, {
        name: createName.trim(),
        adminMemberType: createAdminType,
        pictureFile: createFile,
        token,
      });
      if (created) {
        setRows((prev) => [created, ...(prev || [])]);
      }
      setCreateOpen(false);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not create team. Please try again.";
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteTeam?.publicId) return;
    setDeletingTeam(true);
    setDeleteError("");
    try {
      await deleteTeam(api, confirmDeleteTeam.publicId, token);
      setRows((prev) =>
        (prev || []).filter((t) => t.publicId !== confirmDeleteTeam.publicId),
      );
      setJoinNotice("Team deleted.");
      setConfirmDeleteTeam(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not delete team. Please try again.";
      setDeleteError(message);
    } finally {
      setDeletingTeam(false);
    }
  };

  const currentUserId = user?.publicId;
  const isAdminInTeam = (team) => {
    if (!currentUserId) return false;
    const member = (team?.members || []).find(
      (m) => m.userPublicId === currentUserId,
    );
    return Boolean(member?.isAdmin);
  };

  const filteredRows =
    teamFilter === "admin" ? rows.filter(isAdminInTeam) : rows;

  const columns = [
    {
      key: "picture",
      label: "Team",
      width: "1fr",
      headerClassName: "team-col",
      className: "team-col",
      render: (row) => (
        <div className="cell-team">
          <img
            className="cell-team-photo"
            src={row.pictureUrl || "/icons/Logo_PNG.png"}
            alt={row.name || "Team"}
          />
            <div className="cell-team-meta">
              <div className="cell-team-name">{row.name}</div>
              <div className="team-invite">
                <span className="team-invite-code">{row.inviteCode || "-"}</span>
                <button
                  type="button"
                  className="invite-copy"
                  aria-label="Copy invitation code"
                  onClick={(e) => handleCopyTeamId(e, row)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path
                      d="M9 4h10v14H9z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M5 8h10v14H5z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      opacity="0.5"
                    />
                    <path
                      d="M7 10h6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ),
      },
    {
      key: "admins",
      label: "Admins",
      width: "1fr",
      headerClassName: "team-admins-col",
      className: "team-admins-col",
      render: (row) => {
        const admins = row.members?.filter((m) => m.isAdmin) || [];
        return (
          <div className="cell-avatars">
            {admins.length === 0 && <span>-</span>}
            {admins.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Admin"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Admin"}
                />
                <span>{m.name || m.username || "Admin"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "sportsmen",
      label: "Anglers",
      width: "1fr",
      render: (row) => {
        const sportsmen =
          row.members?.filter((m) => m.memberType === "SPORTSMAN" && !m.isAdmin) ||
          [];
        return (
          <div className="cell-avatars">
            {sportsmen.length === 0 && <span>-</span>}
            {sportsmen.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Angler"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Angler"}
                />
                <span>{m.name || m.username || "Angler"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "viewers",
      label: "Viewers",
      width: "1fr",
      render: (row) => {
        const viewers =
          row.members?.filter((m) => m.memberType === "VIEWER") || [];
        return (
          <div className="cell-avatars">
            {viewers.length === 0 && <span>-</span>}
            {viewers.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Viewer"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Viewer"}
                />
                <span>{m.name || m.username || "Viewer"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "menu",
      label: "",
      width: "72px",
      headerClassName: "cell-right",
      className: "cell-right",
      render: (row) => (
        <div className="team-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="team-more"
            aria-label="Edit team"
            onClick={(e) => handleMenuToggle(e, row.publicId)}
          >
            ...
          </button>
          {openMenuId === row.publicId && (
            <div className="team-menu-popover">
              <button type="button" onClick={(e) => handleEditTeam(e, row)}>
                <span className="team-menu-icon" aria-hidden="true">
                  ✎
                </span>
                Edit Team
              </button>
              <button type="button" onClick={(e) => handleCopyTeamId(e, row)}>
                <span className="team-menu-icon" aria-hidden="true">
                  ⧉
                </span>
                Copy TeamID
              </button>
              {isAdminInTeam(row) && (
                <button type="button" onClick={(e) => handleRemoveTeam(e, row)}>
                  <span className="team-menu-icon" aria-hidden="true">
                    🗑
                  </span>
                  Remove Team
                </button>
              )}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="team-list-header">
        <h2>Teams</h2>
        <div className="team-list-toolbar">
          <div className="member-toggle team-filter">
            <button
              type="button"
              className={`member-toggle-btn${
                teamFilter === "all" ? " active" : ""
              }`}
              onClick={() => setTeamFilter("all")}
            >
              All Teams
            </button>
            <button
              type="button"
              className={`member-toggle-btn${
                teamFilter === "admin" ? " active" : ""
              }`}
              onClick={() => setTeamFilter("admin")}
            >
              My Teams
            </button>
          </div>
          <div className="team-list-actions">
            <button type="button" className="btn btn-ghost" onClick={handleOpenJoin}>
              Join Team
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleOpenCreate}
            >
              Create New Team
            </button>
          </div>
        </div>
      </div>
      {joinNotice && <div className="team-list-alert success">{joinNotice}</div>}
      {loading && <Loader />}
      {!loading && filteredRows.length === 0 && (
        <EmptyState
          title="No teams"
          message={
            teamFilter === "admin"
              ? "You're not an admin in any teams yet."
              : "You are not in any team."
          }
        />
      )}
      {!loading && filteredRows.length > 0 && (
        <div className="team-table">
          <Table
            columns={columns}
            rows={filteredRows}
            onRowClick={(row) => navigate(`/teams/${row.publicId}/sessions`)}
          />
        </div>
      )}
      {editingTeam && (
        <div
          className="title-modal-backdrop"
        >
          <div
            className="title-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <TeamEditPage
              teamIdOverride={editingTeam.publicId}
              onClose={() => setEditingTeam(null)}
              onSaved={handleTeamSaved}
            />
          </div>
        </div>
      )}
      {createOpen && (
        <div className="title-modal-backdrop">
          <div
            className="title-modal create-team-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="title-modal-header">
              <div className="title-modal-title">Create Team</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <label className="title-modal-label is-primary" htmlFor="team-name">
                Team Name
              </label>
              <input
                id="team-name"
                className="title-modal-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Enter team name"
                disabled={creating}
              />
              <label className="title-modal-label is-secondary">
                Admin Member Type
              </label>
              <div className="modal-switch">
                <button
                  type="button"
                  className={`modal-switch-btn${
                    createAdminType === "SPORTSMAN" ? " active" : ""
                  }`}
                  onClick={() => setCreateAdminType("SPORTSMAN")}
                  disabled={creating}
                >
                  Angler
                </button>
                <button
                  type="button"
                  className={`modal-switch-btn${
                    createAdminType === "VIEWER" ? " active" : ""
                  }`}
                  onClick={() => setCreateAdminType("VIEWER")}
                  disabled={creating}
                >
                  Viewer
                </button>
              </div>
              <label className="title-modal-label is-primary" htmlFor="team-picture">
                Team Picture
              </label>
              <input
                id="team-picture"
                className="title-modal-input"
                type="file"
                accept="image/*"
                onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                disabled={creating}
              />
              {createError && (
                <div className="title-modal-error">{createError}</div>
              )}
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateTeam}
                disabled={creating}
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteTeam && (
        <div className="title-modal-backdrop">
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Delete team?</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmDeleteTeam(null)}
                disabled={deletingTeam}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <div>
                This will permanently delete the team and all its sessions. This
                action cannot be undone.
              </div>
              {deleteError && (
                <div className="title-modal-error">{deleteError}</div>
              )}
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmDeleteTeam(null)}
                disabled={deletingTeam}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmDelete}
                disabled={deletingTeam}
              >
                {deletingTeam ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {joinOpen && (
        <div className="title-modal-backdrop">
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Join Team</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setJoinOpen(false)}
                disabled={joining}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <label className="title-modal-label is-primary" htmlFor="join-code">
                Invite Code
              </label>
              <input
                id="join-code"
                className="title-modal-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Enter invite code"
                disabled={joining}
              />
              {joinError && <div className="title-modal-error">{joinError}</div>}
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setJoinOpen(false)}
                disabled={joining}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleJoinTeam}
                disabled={joining}
              >
                {joining ? "Joining..." : "Join"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamListPage;



